import { Component, Input, OnChanges, Optional, SimpleChanges } from '@angular/core'
import { HttpErrorResponse } from '@angular/common/http'
import { MatBottomSheetRef } from '@angular/material/bottom-sheet'
import { CallerService } from '../services/caller.service'
import { CorpMemberState, ProfileLink } from '../ngrx-store/app.state'
import { newMessage } from '../ngrx-store/actions/corp-member.actions'
import { AnalyticsService } from '../services/analytics.service'
import { chatIdentityOf } from '../utils/corp-member.util'

/**
 * Presentational profile content, reused both on the full `/p/:route-id`
 * page and inside the chat avatar bottom sheet — fetches by whatever slug
 * it's given rather than reading the route itself.
 */
@Component({
  selector: 'app-public-profile-card',
  templateUrl: './public-profile-card.component.html',
  styleUrls: ['./public-profile-card.component.css'],
})
export class PublicProfileCardComponent implements OnChanges {
  @Input() corperSlug: string | null = null

  corpMember?: CorpMemberState
  externalLinks: ProfileLink[] = []
  isLoading = false
  loadError = false

  constructor(
    public callerService: CallerService,
    private analyticsService: AnalyticsService,
    @Optional() private bottomSheetRef?: MatBottomSheetRef<unknown>,
  ) {}

  get hasServiceDetails(): boolean {
    return !!(
      this.corpMember?.PPA?.name ||
      this.corpMember?.batch ||
      this.corpMember?.stream ||
      this.corpMember?.travel_from_state
    )
  }

  // Only for other logged-in users viewing someone else's profile.
  get showStartChat(): boolean {
    const theirId = chatIdentityOf(this.corpMember)
    const myId = chatIdentityOf(this.callerService.corpMember)
    return this.callerService.isLoggedIn && !!theirId && theirId !== myId
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['corperSlug']) {
      this.loadProfile()
    }
  }

  loadProfile(): void {
    if (!this.corperSlug) {
      this.corpMember = undefined
      return
    }

    this.isLoading = true
    this.loadError = false
    this.callerService.getCorpMemberProfile({ id: this.corperSlug }).subscribe({
      next: (res: any) => {
        if (res.body?.data) {
          this.corpMember = res.body.data
          this.externalLinks = res.body.data.ProfileLinks ?? []
        }
        this.isLoading = false
      },
      error: (err: HttpErrorResponse) => {
        if (err.status !== 404) {
          this.loadError = true
        }
        this.isLoading = false
      },
    })
  }

  startChat(): void {
    const recipientId = chatIdentityOf(this.corpMember)
    const initiatorId = chatIdentityOf(this.callerService.corpMember)

    // state_code is display-only here (used for the chat list's avatar/subtitle) — must not
    // block starting the chat if it's missing.
    if (!recipientId || !initiatorId) {
      this.callerService.showNotification("Sorry, we couldn't start a chat with this user.")
      return
    }

    this.analyticsService.chatStarted('profile')
    this.callerService.store.dispatch(newMessage({
      recipient_id: recipientId,
      recipient_state_code: this.corpMember?.state_code ?? '',
      recipient_name: this.corpMember?.first_name ?? this.corpMember?.nickname,
      initiator_state_code: this.callerService.corpMember.state_code ?? '',
      initiator_name: this.callerService.corpMember.first_name,
      initiator_id: initiatorId,
      chat_display_name: this.corpMember?.first_name ?? this.corpMember?.nickname,
      chat_display_state_code: this.corpMember?.state_code ?? '',
      source: 'profile',
    }))

    this.bottomSheetRef?.dismiss()
  }
}
